import { IconButton, Stack } from '@mui/material';
import { useModal } from 'components/modals';
import {
  saveBrandVoiceId,
  saveTargetAudienceId,
} from 'features/aiWriter/AiWriterSidebar/steps/chat/chatStore';
import {
  ChatOptimizePromptButton,
  IMPROVE_PROMPT_OUTPUT_TYPE,
} from 'features/aiWriter/chat/ChatOptimizePromptButton';
import { ChatSendButton } from 'features/aiWriter/chat/ChatSendButton';
import { ChatSettingsPopover } from 'features/aiWriter/chat/ChatSettingsPopover';
import { ChatTargetAudienceSelect } from 'features/aiWriter/chat/ChatTargetAudienceSelect';
import { GenericMessageInput } from 'features/aiWriter/chat/GenericMessageInput';
import { useIsGpt4Disabled } from 'features/aiWriter/chat/useIsGpt4Disabled';
import { useGetProjectOrPreselectedLanguageAndCountry } from 'features/aiWriter/hooks/useGetProjectOrPreselectedLanguageAndCountry';
import { ExtendedChatInputBrandVoiceSelect } from 'features/brand-voice/ExtendedChatInputBrandVoiceSelect';
import { SmallerCountryFlag } from 'features/document-top-bar/DocumentPageTopBar';
import { MessageConfig } from 'features/homePage/chatForm/CreateChatForm';
import { LanguageSelect } from 'features/homePage/chatForm/LanguageSelect';
import { InformationButton } from 'features/information/apply-information/InformationButton';
import { usePromptOptimizerControlled } from 'features/textGenerator/promptOptimizer/usePromptOptimizerContorlled';
import useUpdateSubscriptionModal from 'features/updateSubscriptionModal/hook/useUpdateSubscriptionModal';
import { usePostHog } from 'posthog-js/react';
import { KeyboardEvent, useRef } from 'react';
import {
  GPT_MODELS,
  GptModel,
} from 'services/backofficeIntegration/http/endpoints/aiWriter/httpCreateConversation';
import { InformationDto } from 'services/backofficeIntegration/http/endpoints/infomration/httpGetInformationList';
import { GAEvents } from 'services/tracking/GAEvents';
import gtmIds from 'services/tracking/GTMIds';
import { withGtmInteraction } from 'services/tracking/withGtmInteraction';
import styled from 'styled-components';

type Props = {
  modelId: string;
  isSending: boolean;
  value: MessageConfig;
  sendButtonConfig: { gtmId: string; disabled?: boolean };

  onChange: (message: MessageConfig) => void;
  onSend: (text: string) => void;
  onLanguageModelChange: (languageModelId: string) => void;
};

export const HomeMessageInput = ({
  modelId,
  isSending,
  value,
  onChange,
  onSend,
  onLanguageModelChange,
}: Props) => {
  const postHog = usePostHog();
  const showUpgradeSubscriptionModal = useUpdateSubscriptionModal();
  const isGpt4Disabled = useIsGpt4Disabled();
  const { text, gptModel, brandVoiceId, targetAudienceId } = value;
  const { language, country } = useGetProjectOrPreselectedLanguageAndCountry(modelId);
  const { isOpen: isAnyModalOpen } = useModal();

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    isUndoVisible,
    setInitialPrompt,
    optimizePromptMutation: { mutate: optimizePrompt, isLoading: isOptimizationLoading },
    undoPromptOptimization,
    setIsUndoVisible,
  } = usePromptOptimizerControlled({
    prompt: text,
    setPrompt: (prompt: string) => onChange({ ...value, text: prompt }),
    locale: { language, country },
    initialPrompt: '',
    outputType: IMPROVE_PROMPT_OUTPUT_TYPE,
  });

  const autoFocusTextarea = !isAnyModalOpen;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.shiftKey || e.key !== 'Enter') {
      return;
    }

    e.preventDefault();

    GAEvents.sentChatMessage({ source: 'enter' });
    postHog?.capture('CF - chat message sent via overview');
    onSend(text);
  };

  const handleGptModelChange = (gptModel: GptModel) => {
    const newModel = gptModel;
    if (!newModel) {
      return;
    }

    if (isGpt4Disabled && newModel === GPT_MODELS.GPT_4) {
      showUpgradeSubscriptionModal();
      return;
    }

    onChange({ ...value, gptModel: newModel });
    focusInput();
  };

  const handleTemplateSelect = (template: string) => {
    onChange({ ...value, text: template });
    onSend(template);
    focusInput();

    if (isUndoVisible) {
      setIsUndoVisible(false);
    }
  };

  const handleBrandVoiceChange = (brandVoiceId: string | null) => {
    onChange({ ...value, brandVoiceId });
    saveBrandVoiceId(brandVoiceId);
    focusInput();
  };

  const handleInformationChange = (informationList: InformationDto[]) => {
    onChange({ ...value, informationList });
  };

  const handleTextFieldChange = (text: string) => {
    onChange({ ...value, text });

    if (isUndoVisible) {
      setIsUndoVisible(false);
    }
  };

  const handleOptimizeClick = () => {
    focusInput();
    setInitialPrompt(text);
    optimizePrompt();
  };

  const handleSettingsChange = () => {
    focusInput();
  };

  const handleBrandVoiceSelectIconMouseDown = (e: React.MouseEvent) => {};

  const handleTargetAudienceChange = (targetAudienceId: string | null) => {
    onChange({ ...value, targetAudienceId });
    saveTargetAudienceId(targetAudienceId);
    focusInput();
  };

  const focusInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  return (
    <GenericMessageInput
      ref={inputRef}
      value={text}
      disabled={isSending}
      onChange={handleTextFieldChange}
      onSend={onSend}
      onKeyDown={handleKeyDown}
      minRows={3}
      maxRows={3}
      autoFocus={autoFocusTextarea}
      testId="chatflash-text-input-field"
    >
      <LeftContainer>
        <ChatSettingsPopover
          onSettingsChange={handleSettingsChange}
          onMouseDown={handleBrandVoiceSelectIconMouseDown}
          gptModel={gptModel}
          onGptModelChange={handleGptModelChange}
          isGpt4Disabled={isGpt4Disabled}
          modelId={modelId}
          onTemplateSelection={handleTemplateSelect}
        />
        <LanguageSelect
          value={modelId}
          onSelect={onLanguageModelChange}
          customTrigger={props => (
            <BorderedIconButton
              {...withGtmInteraction(gtmIds.aiWriter.languageSelector)}
              size="small"
              onClick={props.onClick}
            >
              <SmallerCountryFlag
                country={props.embeddingModel?.country}
                language={props.embeddingModel?.language}
                coverParent
              />
            </BorderedIconButton>
          )}
        />
        <ExtendedChatInputBrandVoiceSelect
          selectedBrandVoiceId={brandVoiceId ?? undefined}
          targetLanguage={language}
          targetCountry={country}
          menuOpeningDirection="bottom"
          onMouseDown={handleBrandVoiceSelectIconMouseDown}
          onBrandVoiceChange={handleBrandVoiceChange}
        />
        <ChatTargetAudienceSelect
          targetLanguage={language}
          targetCountry={country}
          menuOpeningDirection="bottom"
          onTargetAudienceIdChange={handleTargetAudienceChange}
          selectedTargetAudienceId={targetAudienceId ?? undefined}
        />
        <InformationButton
          onMouseDown={handleBrandVoiceSelectIconMouseDown}
          onInformationChange={handleInformationChange}
        />
      </LeftContainer>

      <Stack direction="row">
        <ChatOptimizePromptButton
          onMouseDown={handleBrandVoiceSelectIconMouseDown}
          prompt={text}
          undoVisible={isUndoVisible}
          loading={isOptimizationLoading}
          onOptimizeClick={handleOptimizeClick}
          onUndoClick={undoPromptOptimization}
        />
        <ChatSendButton
          {...withGtmInteraction('GTM_aiWriter_projectOverview_chat_sendMessage')}
          isSending={isSending}
          onSend={() => onSend(text)}
        />
      </Stack>
    </GenericMessageInput>
  );
};

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings.one};
`;

const BorderedIconButton = styled(IconButton)`
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 50%;
`;
